'use client';

import type { FragmentType } from 'src/gql';
import { useMemo } from 'react';
import { createColumnConfigHelper, DataTableFilter, useDataTableFilters } from '@savvy-kit/templates/data-table-filter';
import { isNil } from 'es-toolkit';
import { graphql, unmaskFragment } from 'src/gql';

import type { ColumnOption, FiltersState, ResultCountConfig } from '@savvy-kit/templates/data-table-filter';

import type { SavedAccountFilter } from '../hooks/use-saved-account-filters';
import type { StrategyType } from '../utils/accounts-url-state';
import { useAccountsParams } from '../utils/accounts-url-state';

const BALANCE_RANGE_COLUMN_ID = 'balanceRange';
const STRATEGY_TYPE_COLUMN_ID = 'strategyType';
const STRATEGY_COLUMN_ID = 'strategy';
const FEE_SCHEDULE_COLUMN_ID = 'feeSchedule';
const HOUSEHOLD_COLUMN_ID = 'household';
const PRIMARY_ADVISOR_COLUMN_ID = 'primaryAdvisor';
export const UNASSIGNED_ADVISOR_VALUE = 'unassigned';

const BALANCE_RANGE_MIN = 0;
const BALANCE_RANGE_MAX = 2_000_000;

const STRATEGY_TYPE_OPTIONS: readonly ColumnOption[] = [
  { value: 'first_party', label: 'First Party' },
  { value: 'third_party', label: 'Third Party' },
  { value: 'unassigned', label: 'None (No Strategy)' },
];

type AccountFilterRow = {
  balanceRange: number;
  strategyType: string[];
  strategy: string[];
  feeSchedule: string[];
  household: string[];
  primaryAdvisor: string[];
};

function getBalanceRangeFilter(
  min: number | null,
  max: number | null,
): {
  operator: 'is between' | 'is greater than or equal to' | 'is less than or equal to';
  values: number[];
} | null {
  if (!isNil(min) && !isNil(max)) {
    return {
      operator: 'is between',
      values: [min, max],
    };
  }

  if (!isNil(min)) {
    return {
      operator: 'is greater than or equal to',
      values: [min],
    };
  }

  if (!isNil(max)) {
    return {
      operator: 'is less than or equal to',
      values: [max],
    };
  }

  return null;
}

export function stateToFilters(
  balanceMin: number | null,
  balanceMax: number | null,
  strategyType: readonly StrategyType[],
  strategy: readonly string[],
  feeSchedule: readonly string[],
  household: readonly string[],
  advisorIds: readonly string[],
  includeUnassignedAdvisor: boolean,
): FiltersState {
  const filters: FiltersState = [];

  const balanceRangeFilter = getBalanceRangeFilter(balanceMin, balanceMax);
  if (!isNil(balanceRangeFilter)) {
    filters.push({
      columnId: BALANCE_RANGE_COLUMN_ID,
      type: 'number',
      operator: balanceRangeFilter.operator,
      values: balanceRangeFilter.values,
    });
  }

  if (strategyType.length > 0) {
    filters.push({
      columnId: STRATEGY_TYPE_COLUMN_ID,
      type: 'multiOption',
      operator: strategyType.length > 1 ? 'include any of' : 'include',
      values: [...strategyType],
    });
  }

  if (strategy.length > 0) {
    filters.push({
      columnId: STRATEGY_COLUMN_ID,
      type: 'multiOption',
      operator: strategy.length > 1 ? 'include any of' : 'include',
      values: [...strategy],
    });
  }

  if (feeSchedule.length > 0) {
    filters.push({
      columnId: FEE_SCHEDULE_COLUMN_ID,
      type: 'multiOption',
      operator: feeSchedule.length > 1 ? 'include any of' : 'include',
      values: [...feeSchedule],
    });
  }

  if (household.length > 0) {
    filters.push({
      columnId: HOUSEHOLD_COLUMN_ID,
      type: 'multiOption',
      operator: household.length > 1 ? 'include any of' : 'include',
      values: [...household],
    });
  }

  const primaryAdvisorValues = includeUnassignedAdvisor ? [UNASSIGNED_ADVISOR_VALUE, ...advisorIds] : [...advisorIds];
  if (primaryAdvisorValues.length > 0) {
    filters.push({
      columnId: PRIMARY_ADVISOR_COLUMN_ID,
      type: 'multiOption',
      operator: primaryAdvisorValues.length > 1 ? 'include any of' : 'include',
      values: primaryAdvisorValues,
    });
  }

  return filters;
}

function isStrategyType(value: string): value is StrategyType {
  return value === 'first_party' || value === 'third_party' || value === 'unassigned';
}

function stringValuesOf(filter: FiltersState[number] | undefined): string[] {
  return !isNil(filter) && filter.type === 'multiOption'
    ? filter.values.filter((value): value is string => typeof value === 'string')
    : [];
}

export function filtersToState(filters: FiltersState): {
  balanceMin: number | null;
  balanceMax: number | null;
  strategyType: StrategyType[];
  strategy: string[];
  feeSchedule: string[];
  household: string[];
  advisorIds: string[];
  includeUnassignedAdvisor: boolean;
} {
  const balanceRangeFilter = filters.find((filter) => filter.columnId === BALANCE_RANGE_COLUMN_ID);
  const strategyTypeFilter = filters.find((filter) => filter.columnId === STRATEGY_TYPE_COLUMN_ID);
  const strategyFilter = filters.find((filter) => filter.columnId === STRATEGY_COLUMN_ID);
  const feeScheduleFilter = filters.find((filter) => filter.columnId === FEE_SCHEDULE_COLUMN_ID);
  const householdFilter = filters.find((filter) => filter.columnId === HOUSEHOLD_COLUMN_ID);
  const primaryAdvisorFilter = filters.find((filter) => filter.columnId === PRIMARY_ADVISOR_COLUMN_ID);
  const primaryAdvisorValues = stringValuesOf(primaryAdvisorFilter);

  const balanceRange = (() => {
    if (isNil(balanceRangeFilter) || balanceRangeFilter.type !== 'number') {
      return { min: null, max: null };
    }

    const firstValue = balanceRangeFilter.values[0];
    const secondValue = balanceRangeFilter.values[1];

    if (balanceRangeFilter.operator === 'is between') {
      return {
        min: typeof firstValue === 'number' ? firstValue : null,
        max: typeof secondValue === 'number' ? secondValue : null,
      };
    }

    if (balanceRangeFilter.operator === 'is greater than or equal to') {
      return {
        min: typeof firstValue === 'number' ? firstValue : null,
        max: null,
      };
    }

    if (balanceRangeFilter.operator === 'is less than or equal to') {
      return {
        min: null,
        max: typeof firstValue === 'number' ? firstValue : null,
      };
    }

    return { min: null, max: null };
  })();

  return {
    balanceMin: balanceRange.min,
    balanceMax: balanceRange.max,
    strategyType:
      !isNil(strategyTypeFilter) && strategyTypeFilter.type === 'multiOption'
        ? strategyTypeFilter.values.filter(
            (value): value is StrategyType => typeof value === 'string' && isStrategyType(value),
          )
        : [],
    strategy: stringValuesOf(strategyFilter),
    feeSchedule: stringValuesOf(feeScheduleFilter),
    household: stringValuesOf(householdFilter),
    advisorIds: primaryAdvisorValues.filter((value) => value !== UNASSIGNED_ADVISOR_VALUE),
    includeUnassignedAdvisor: primaryAdvisorValues.includes(UNASSIGNED_ADVISOR_VALUE),
  };
}

export const AccountsFiltersFragment = graphql(/* GraphQL */ `
  fragment AccountsFilters on AccountFilterOptions {
    strategies
    feeSchedules
    households {
      id
      name
    }
    primaryAdvisors {
      id
      firstName
      lastName
    }
  }
`);

type Props = {
  readonly accountFilterOptionsFrag: FragmentType<typeof AccountsFiltersFragment> | null | undefined;
  readonly savedFilterProps?: {
    readonly atLimit: boolean;
    readonly activePresetName: string | null;
    readonly isModified: boolean;
    readonly hasFilters: boolean;
    readonly existingNames: readonly string[];
    readonly savedFilters: readonly SavedAccountFilter[];
    readonly onSave: (name: string) => void;
    readonly onUpdate: (id: string) => void;
  };
  readonly resultCount?: ResultCountConfig;
};

export function AccountsFilters({ accountFilterOptionsFrag, savedFilterProps, resultCount }: Props) {
  const accountFilterOptions = accountFilterOptionsFrag
    ? unmaskFragment(AccountsFiltersFragment, accountFilterOptionsFrag)
    : null;
  const {
    params,
    setBalanceRange,
    setStrategyType,
    setStrategy,
    setFeeSchedule,
    setHousehold,
    setAdvisorIds,
    setIncludeUnassignedAdvisor,
  } = useAccountsParams();

  const strategyOptions = useMemo<ColumnOption[]>(
    () => (accountFilterOptions?.strategies ?? []).map((strategy) => ({ value: strategy, label: strategy })),
    [accountFilterOptions?.strategies],
  );

  const feeScheduleOptions = useMemo<ColumnOption[]>(
    () => (accountFilterOptions?.feeSchedules ?? []).map((feeSchedule) => ({ value: feeSchedule, label: feeSchedule })),
    [accountFilterOptions?.feeSchedules],
  );

  const householdOptions = useMemo<ColumnOption[]>(
    () =>
      (accountFilterOptions?.households ?? []).map((household) => ({
        value: household.id,
        label: household.name,
      })),
    [accountFilterOptions?.households],
  );

  const primaryAdvisorOptions = useMemo<ColumnOption[]>(
    () => [
      { value: UNASSIGNED_ADVISOR_VALUE, label: 'Unassigned' },
      ...(accountFilterOptions?.primaryAdvisors ?? []).map((advisor) => ({
        value: advisor.id,
        label: `${advisor.firstName} ${advisor.lastName}`.trim(),
      })),
    ],
    [accountFilterOptions?.primaryAdvisors],
  );

  const columnsConfig = useMemo(() => {
    const dtf = createColumnConfigHelper<AccountFilterRow>();
    return [
      dtf
        .number()
        .id(BALANCE_RANGE_COLUMN_ID)
        .accessor((row: AccountFilterRow) => row.balanceRange)
        .displayName('Balance Range')
        .min(BALANCE_RANGE_MIN)
        .max(BALANCE_RANGE_MAX)
        .build(),
      dtf
        .multiOption()
        .id(STRATEGY_TYPE_COLUMN_ID)
        .accessor((row: AccountFilterRow) => row.strategyType)
        .displayName('Strategy Type')
        .options([...STRATEGY_TYPE_OPTIONS])
        .build(),
      dtf
        .multiOption()
        .id(STRATEGY_COLUMN_ID)
        .accessor((row: AccountFilterRow) => row.strategy)
        .displayName('Strategy')
        .options(strategyOptions)
        .build(),
      dtf
        .multiOption()
        .id(FEE_SCHEDULE_COLUMN_ID)
        .accessor((row: AccountFilterRow) => row.feeSchedule)
        .displayName('Fee Schedule')
        .options(feeScheduleOptions)
        .build(),
      dtf
        .multiOption()
        .id(HOUSEHOLD_COLUMN_ID)
        .accessor((row: AccountFilterRow) => row.household)
        .displayName('Household')
        .options(householdOptions)
        .build(),
      dtf
        .multiOption()
        .id(PRIMARY_ADVISOR_COLUMN_ID)
        .accessor((row: AccountFilterRow) => row.primaryAdvisor)
        .displayName('Primary Advisor')
        .options(primaryAdvisorOptions)
        .build(),
    ];
  }, [strategyOptions, feeScheduleOptions, householdOptions, primaryAdvisorOptions]);

  const filters = useMemo(
    () =>
      stateToFilters(
        params.balanceMin ?? null,
        params.balanceMax ?? null,
        params.strategyType,
        params.strategy,
        params.feeSchedule,
        params.household,
        params.advisorIds,
        params.includeUnassignedAdvisor,
      ),
    [
      params.balanceMin,
      params.balanceMax,
      params.strategyType,
      params.strategy,
      params.feeSchedule,
      params.household,
      params.advisorIds,
      params.includeUnassignedAdvisor,
    ],
  );

  const onFiltersChange = useMemo(
    () => (next: FiltersState | ((prev: FiltersState) => FiltersState)) => {
      const nextFilters = typeof next === 'function' ? next(filters) : next;
      const nextState = filtersToState(nextFilters);
      setBalanceRange(nextState.balanceMin, nextState.balanceMax);
      setStrategyType(nextState.strategyType);
      setStrategy(nextState.strategy);
      setFeeSchedule(nextState.feeSchedule);
      setHousehold(nextState.household);
      setAdvisorIds(nextState.advisorIds);
      setIncludeUnassignedAdvisor(nextState.includeUnassignedAdvisor);
    },
    [
      filters,
      setBalanceRange,
      setStrategyType,
      setStrategy,
      setFeeSchedule,
      setHousehold,
      setAdvisorIds,
      setIncludeUnassignedAdvisor,
    ],
  );

  const {
    columns,
    filters: hookFilters,
    actions,
    strategy,
  } = useDataTableFilters({
    strategy: 'server',
    data: [],
    columnsConfig,
    filters,
    onFiltersChange,
    options: {
      [STRATEGY_TYPE_COLUMN_ID]: [...STRATEGY_TYPE_OPTIONS],
      [STRATEGY_COLUMN_ID]: strategyOptions,
      [FEE_SCHEDULE_COLUMN_ID]: feeScheduleOptions,
      [HOUSEHOLD_COLUMN_ID]: householdOptions,
      [PRIMARY_ADVISOR_COLUMN_ID]: primaryAdvisorOptions,
    },
    faceted: {
      [BALANCE_RANGE_COLUMN_ID]: [BALANCE_RANGE_MIN, BALANCE_RANGE_MAX],
    },
  });

  const savedViews = !isNil(savedFilterProps)
    ? {
        atLimit: savedFilterProps.atLimit,
        activeViewName: savedFilterProps.activePresetName,
        isModified: savedFilterProps.isModified,
        hasFilters: savedFilterProps.hasFilters,
        existingNames: [...savedFilterProps.existingNames],
        savedViews: savedFilterProps.savedFilters.map((savedFilter) => ({
          id: savedFilter.id,
          name: savedFilter.name,
        })),
        onSave: savedFilterProps.onSave,
        onUpdate: savedFilterProps.onUpdate,
      }
    : undefined;

  return (
    <DataTableFilter
      columns={columns}
      filters={hookFilters}
      actions={actions}
      strategy={strategy}
      filterValueVariant="compact"
      showClearFilterButton={false}
      searchableColumnIds={[STRATEGY_COLUMN_ID, FEE_SCHEDULE_COLUMN_ID, HOUSEHOLD_COLUMN_ID, PRIMARY_ADVISOR_COLUMN_ID]}
      savedViews={savedViews}
      resultCount={resultCount}
    />
  );
}
